// =============================================================================
// Settings Page - Amministrazione e Configurazione
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useTenantStore } from '../store/tenantStore';
import { Settings, Users, Package, Layers, UserPlus, Building2, Globe, CalendarOff, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { OrganizationTab } from '../components/settings/OrganizationTab';
import { PlanTab } from '../components/settings/PlanTab';
import { ResourcesTab } from '../components/settings/ResourcesTab';
import { ServicesTab } from '../components/settings/ServicesTab';
import { ResourceTypesTab } from '../components/settings/ResourceTypesTab';
import { TeamTab } from '../components/settings/TeamTab';
import { PublicBookingTab } from '../components/settings/PublicBookingTab';
import { AbsencesTab } from '../components/settings/AbsencesTab';
import { SettingsDirtyContext } from '../components/settings/settingsDirty';

type TabId = 'organization' | 'plan' | 'resource-types' | 'resources' | 'absences' | 'services' | 'team' | 'public-booking';

interface Tab {
  id: TabId;
  labelKey: string;
  icon: React.ElementType;
  group: 'admin' | 'operativo';
}

const allTabs: Tab[] = [
  // Gruppo Amministrazione (solo ADMIN/OWNER)
  { id: 'organization', labelKey: 'tabs.organization', icon: Building2, group: 'admin' },
  { id: 'plan', labelKey: 'tabs.plan', icon: CreditCard, group: 'admin' },
  { id: 'team', labelKey: 'tabs.team', icon: UserPlus, group: 'admin' },
  { id: 'public-booking', labelKey: 'tabs.publicBooking', icon: Globe, group: 'admin' },
  // Gruppo Operativo (configurazione risorse e servizi)
  { id: 'resource-types', labelKey: 'tabs.resourceTypes', icon: Layers, group: 'operativo' },
  { id: 'resources', labelKey: 'tabs.resources', icon: Users, group: 'operativo' },
  { id: 'absences', labelKey: 'tabs.absences', icon: CalendarOff, group: 'operativo' },
  { id: 'services', labelKey: 'tabs.services', icon: Package, group: 'operativo' },
];

const GROUP_LABEL_KEYS: Record<string, string> = {
  admin: 'groups.admin',
  operativo: 'groups.config',
};

export function SettingsPage() {
  const { user } = useAuthStore();
  const activeRole = useTenantStore((s) => s.activeRole);
  const { t } = useTranslation('settings');

  // Solo ADMIN e OWNER possono accedere
  if (activeRole && !['ADMIN', 'OWNER'].includes(activeRole)) {
    return <Navigate to="/scheduler" replace />;
  }

  const isAdmin = activeRole === 'ADMIN' || activeRole === 'OWNER';

  // Filtra i tab in base al ruolo
  const visibleTabs = allTabs.filter((tab) => {
    if (tab.group === 'admin') return isAdmin;
    return true;
  });

  const [activeTab, setActiveTab] = useState<TabId>(visibleTabs[0]?.id || 'resource-types');

  // --- Dirty-state tracking: i tab con form inline segnalano modifiche pendenti ---
  const dirtyTabsRef = useRef<Record<string, boolean>>({});
  const [anyDirty, setAnyDirty] = useState(false);

  const registerDirty = useCallback((id: string, dirty: boolean) => {
    if (dirtyTabsRef.current[id] === dirty) return;
    dirtyTabsRef.current = { ...dirtyTabsRef.current, [id]: dirty };
    setAnyDirty(Object.values(dirtyTabsRef.current).some(Boolean));
  }, []);

  // Avvisa prima di abbandonare la pagina con modifiche non salvate
  useEffect(() => {
    if (!anyDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [anyDirty]);

  const changeTab = useCallback(
    (id: TabId) => {
      if (id === activeTab) return;
      if (dirtyTabsRef.current[activeTab] && !window.confirm(t('unsavedConfirm'))) return;
      setActiveTab(id);
    },
    [activeTab, t],
  );

  // Raggruppa i tab visibili per gruppo
  const groups = visibleTabs.reduce<Record<string, Tab[]>>((acc, tab) => {
    if (!acc[tab.group]) acc[tab.group] = [];
    acc[tab.group].push(tab);
    return acc;
  }, {});

  const activeTabMeta = visibleTabs.find((t) => t.id === activeTab);

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Page Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-emerald-50 rounded-lg flex-shrink-0">
              <Settings className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-gray-900 leading-tight">{t('title')}</h1>
              {activeTabMeta && (
                <p className="text-xs text-gray-500 mt-0.5">
                  {t(GROUP_LABEL_KEYS[activeTabMeta.group])} · {t(activeTabMeta.labelKey)}
                </p>
              )}
            </div>
          </div>
          <div className="text-sm text-gray-500 truncate hidden sm:block">
            {user?.firstName} {user?.lastName}
          </div>
        </div>
      </header>

      {/* Tabs Navigation — sticky, raggruppati, scrollabile su mobile */}
      <div className="bg-white border-b border-gray-200 px-6 sticky top-0 z-10 shadow-[0_1px_2px_rgba(0,0,0,0.02)] flex-shrink-0">
        <nav className="flex items-stretch gap-0 overflow-x-auto scrollbar-thin">
          {Object.entries(groups).map(([groupKey, tabs], groupIndex) => (
            <div key={groupKey} className="flex items-stretch">
              {/* Separatore tra gruppi */}
              {groupIndex > 0 && <div className="self-center h-6 w-px bg-gray-200 mx-3" />}

              {/* Label del gruppo */}
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mr-2 self-end mb-3 whitespace-nowrap">
                {t(GROUP_LABEL_KEYS[groupKey])}
              </span>

              {/* Tab del gruppo */}
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => changeTab(tab.id)}
                    className={`
                      relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap
                      ${isActive
                        ? 'text-emerald-700'
                        : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                      }
                    `}
                  >
                    <Icon className={`w-4 h-4 transition-colors ${isActive ? 'text-emerald-600' : 'text-gray-400'}`} />
                    {t(tab.labelKey)}
                    {/* Indicator inferiore */}
                    <span
                      className={`absolute left-2 right-2 bottom-0 h-[2px] rounded-t-full transition-all ${
                        isActive ? 'bg-emerald-600 opacity-100' : 'bg-transparent opacity-0'
                      }`}
                    />
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
      </div>

      {/* Tab Content — ResourcesTab gestisce il proprio layout full-bleed,
          gli altri tab usano un container max-w centrato. */}
      <SettingsDirtyContext.Provider value={{ register: registerDirty }}>
        <main className="flex-1 overflow-auto">
          {activeTab === 'resources' ? (
            <ResourcesTab />
          ) : (
            <div className="max-w-5xl mx-auto px-6 py-8">
              {activeTab === 'organization' && <OrganizationTab />}
              {activeTab === 'plan' && <PlanTab />}
              {activeTab === 'resource-types' && <ResourceTypesTab />}
              {activeTab === 'absences' && <AbsencesTab />}
              {activeTab === 'services' && <ServicesTab />}
              {activeTab === 'team' && <TeamTab />}
              {activeTab === 'public-booking' && <PublicBookingTab />}
            </div>
          )}
        </main>
      </SettingsDirtyContext.Provider>
    </div>
  );
}

export default SettingsPage;
